/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 選定可能型数ウォッチのアグリゲーション/表示用ユーティリティ
 */

import type { CategoryDef, DashboardPayload, SeasonDef } from '../dashboard-types.js';

/** @graph-connects none */
export type Severity = '' | 'warn' | 'danger';

export interface WeightContext {
  selectedSeasons: Set<SeasonDef['key']>;
  excludeBrandSelect: boolean;
  ranges: Record<string, [number, number]>;
  dimensions: DashboardPayload['dimensions'];
  catDim: DashboardPayload['catDim'];
}

/** @graph-connects none */
export function dimensionMultiplier(
  catId: number,
  ranges: Record<string, [number, number]>,
  dimensions: DashboardPayload['dimensions'],
  catDim: DashboardPayload['catDim'],
): number {
  let mult = 1.0;
  for (const d of dimensions) {
    const r = ranges[d.key];
    if (!r) continue;
    if (r[0] === d.min && r[1] === d.max) continue;
    const cr = catDim[catId]?.[d.key];
    if (!cr) continue;
    const overlap = Math.max(0, Math.min(r[1], cr[1]) - Math.max(r[0], cr[0]));
    const span = cr[1] - cr[0];
    mult *= span > 0 ? overlap / span : overlap > 0 ? 1 : 0;
    if (mult === 0) return 0;
  }
  return mult;
}

/** @graph-connects none */
export function applyWeight(baseValue: number, c: CategoryDef, ctx: WeightContext): number {
  let w = 0;
  ctx.selectedSeasons.forEach((k) => {
    w += c.seasonality[k] || 0;
  });
  if (ctx.excludeBrandSelect) w *= 1 - (c.brandSelectShare || 0);
  w *= dimensionMultiplier(c.id, ctx.ranges, ctx.dimensions, ctx.catDim);
  const v = baseValue * w;
  return v >= 0 ? Math.round(v) : -Math.round(-v);
}

/** @graph-connects none */
export function deltaClass(d: number): 'up' | 'down' | 'flat' {
  if (d > 0) return 'up';
  if (d < 0) return 'down';
  return 'flat';
}

/** @graph-connects none */
export function deltaText(d: number): string {
  if (d === 0) return '±0';
  return (d > 0 ? '+' : '') + d.toLocaleString();
}

/** @graph-connects none */
export function severityLabel(s: Severity): string {
  if (s === 'danger') return '危険水準';
  if (s === 'warn') return '注意水準';
  return '健全';
}

/** @graph-connects none */
export function cardClassFor(sev: Severity): string {
  if (sev === 'danger') return 'has-danger';
  if (sev === 'warn') return 'has-warn';
  return '';
}
